/**
 * Call-logs (NOT-15) — React Query hooks. Like the inbox, realtime `call.*`
 * SSE events (handled in `useInboxStream`) drive cache invalidation; polling is
 * a slow fallback that only runs when the SSE stream is down.
 */

import { useCallback, useMemo } from 'react';
import {
  useInfiniteQuery,
  useQueries,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { isApiError, normalizeError, type ApiError } from '@/lib/api/errors';
import { toast } from 'sonner';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { callLogKeys, callOutcomeKeys, contactKeys, taskKeys } from '@/lib/queryKeys';
import { useSseConnected } from '@/stores/inbox.store';
import { callOutcomesApi, callsApi } from './api';
import { counterpartNumber } from './format';
import {
  callLogRowKey,
  flattenPages,
  followUpRowKey,
  nextActionNeededPage,
  nextCallLogsPage,
} from './infinitePages';
import type { CallReportsParams } from './callReportTypes';
import type {
  CallFollowUpDisposition,
  CallOutcomePolarity,
  CallLogItem,
  CallLogsResponse,
  CallFollowUp,
  CallFollowUpCounts,
  CallFollowUpsResponse,
  CallReportParams,
  FollowUpCommitmentInput,
  ActionNeededReportParams,
  ListCallFollowUpsParams,
  ListCallLogsParams,
  UnbookedCallsParams,
} from './types';

/** Fallback poll only while SSE is unavailable (call history is not high-churn). */
const POLL_CALL_LOGS_MS = 60_000;

/**
 * Is this the same list of calls, just a different page of it? Every other
 * param has to agree — a new search or filter asks a different question, and
 * holding the old answer would put one query's calls under another's heading.
 */
export function isPageTurn(previousParams: unknown, params: ListCallLogsParams): boolean {
  if (!previousParams || typeof previousParams !== 'object') return false;
  const before = previousParams as Record<string, unknown>;
  const after = params as Record<string, unknown>;
  const fields = new Set(
    [...Object.keys(before), ...Object.keys(after)].filter((field) => field !== 'page'),
  );
  return [...fields].every((field) => before[field] === after[field]);
}

/**
 * `keepPreviousPage` holds the rows you are looking at while the next page
 * loads: dropping back to a skeleton takes the footer with it, so the buttons
 * you just clicked move out from under the cursor mid-page-turn.
 *
 * It is opt-in, and even then only spans a page turn. Carrying rows across a
 * new search would be worse than a skeleton — `AiCallNotesCard` resolves a call
 * by number through this same hook and waits on `isLoading`, so stale rows
 * would read as "no call for this number" rather than "still looking".
 */
export function useCallLogs(
  params: ListCallLogsParams = {},
  options: { keepPreviousPage?: boolean } = {},
) {
  const sseOpen = useSseConnected();
  return useApiQuery({
    queryKey: callLogKeys.list(params as Record<string, unknown>),
    queryFn: () => callsApi.list(params),
    placeholderData: options.keepPreviousPage
      ? (previous, previousQuery) =>
          isPageTurn(previousQuery?.queryKey.at(-1), params) ? previous : undefined
      : undefined,
    refetchInterval: sseOpen ? false : POLL_CALL_LOGS_MS,
    showErrorToast: false,
  });
}

/** Most calls one contact history pulls per number — the calling service caps a page at 100. */
const CONTACT_CALLS_LIMIT = 100;

/**
 * The digits that identify a number regardless of how it was typed: the last
 * ten for a North American number (so "+1 (548) 798-0053" and "5487980053"
 * agree), every digit otherwise.
 */
function phoneKey(value?: string | null): string | null {
  const digits = (value ?? '').replace(/\D/g, '');
  if (digits.length < 7) return null;
  return digits.length >= 10 ? digits.slice(-10) : digits;
}

/**
 * Every call with one contact, newest first, across all of their numbers.
 *
 * The calling service cannot filter by contact — a 2.0 contact id is not
 * something it indexes — so this asks for each number through the same search
 * the call-log page uses, then keeps only the calls whose OTHER party is that
 * number. The search matches our own line too, and a partial match on a
 * shorter number, so the check is not optional.
 */
export function useContactCallLogs(
  phones: Array<string | null | undefined>,
  /** How many pages of each number's history to hold; raising it loads older calls. */
  pages = 1,
) {
  const sseOpen = useSseConnected();
  const queryClient = useQueryClient();
  const keys = [...new Set(phones.map(phoneKey).filter((key): key is string => !!key))].sort();
  const paramsFor = (key: string, page: number): ListCallLogsParams =>
    // Page 1 keeps the key it has always had, so the overview preview and
    // this share one cache entry.
    page === 1
      ? { search: key, limit: CONTACT_CALLS_LIMIT }
      : { search: key, limit: CONTACT_CALLS_LIMIT, page };
  // A later page is only asked for once the page before it said there is one.
  const hasPageAfter = (key: string, page: number) =>
    hasNextCallLogsPage(
      queryClient.getQueryData<CallLogsResponse>(
        callLogKeys.list(paramsFor(key, page) as Record<string, unknown>),
      ),
    );
  const requests = keys.flatMap((key) =>
    Array.from({ length: Math.max(1, pages) }, (_, index) => ({ key, page: index + 1 })),
  );

  return useQueries({
    queries: requests.map(({ key, page }) => {
      const params = paramsFor(key, page);
      return {
        queryKey: callLogKeys.list(params as Record<string, unknown>),
        queryFn: () => callsApi.list(params),
        enabled: page === 1 || hasPageAfter(key, page - 1),
        refetchInterval: sseOpen ? (false as const) : POLL_CALL_LOGS_MS,
        // A user who can see the contact but not call history (no
        // messages:view) gets a 403 here. The feed simply has no calls then,
        // rather than a toast on every contact they open.
        retry: false,
        meta: { suppressToast: true },
      };
    }),
    combine: (results) => {
      const wanted = new Set(keys);
      const byId = new Map<string, CallLogItem>();
      // The last page held per number decides whether it has older calls.
      const lastPage = new Map<string, CallLogsResponse | undefined>();
      results.forEach((result, index) => {
        const { key } = requests[index];
        if (result.data) lastPage.set(key, result.data);
        for (const call of result.data?.data?.callLogs ?? []) {
          const callKey = phoneKey(counterpartNumber(call));
          if (callKey && wanted.has(callKey)) byId.set(call.callControlId, call);
        }
      });
      return {
        calls: [...byId.values()].sort(
          (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
        ),
        isLoading: results.some((result) => result.isLoading),
        isFetchingMore: results.some(
          (result, index) => requests[index].page > 1 && result.isFetching,
        ),
        hasMore: [...lastPage.values()].some(hasNextCallLogsPage),
      };
    },
  });
}

/** Whether the calling service has another page after this one. */
export function hasNextCallLogsPage(response?: CallLogsResponse | null): boolean {
  const pagination = response?.data?.pagination;
  if (!pagination) return false;
  return pagination.hasNextPage ?? pagination.currentPage < pagination.totalPages;
}

export function useCallDetail(callControlId: string | null) {
  return useApiQuery({
    queryKey: callLogKeys.detail(callControlId ?? ''),
    queryFn: () => callsApi.detail(callControlId as string),
    enabled: !!callControlId,
    showErrorToast: false,
  });
}

/** The calls that did not turn into work, for the window the report covers.
 *  Disabled until the report has resolved its period — asking for a window we
 *  have not been told yet would list a different one than the chart shows. */
export function useUnbookedCalls(params: UnbookedCallsParams, options: { enabled?: boolean } = {}) {
  return useApiQuery({
    queryKey: callLogKeys.unbooked(params as Record<string, unknown>),
    queryFn: () => callsApi.unbooked(params),
    enabled: options.enabled && !!params.startDate && !!params.endDate,
    showErrorToast: false,
  });
}

export function useCallReport(params: CallReportParams = {}, options: { enabled?: boolean } = {}) {
  return useApiQuery({
    queryKey: callLogKeys.report(params as Record<string, unknown>),
    queryFn: () => callsApi.report(params),
    enabled: options.enabled,
    showErrorToast: false,
  });
}

/** Generate (or save) the AI call insight, then refresh the call's detail. */
export function useGenerateSummary(callControlId: string | null) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body?: Record<string, unknown>) => callsApi.summary(callControlId as string, body),
    onSuccess: () => {
      if (callControlId) {
        void qc.invalidateQueries({
          queryKey: callLogKeys.detail(callControlId),
        });
      }
    },
  });
}

/** Request an on-demand transcription, then refresh the call's detail. */
export function useTranscribe(callControlId: string | null) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: () => callsApi.transcribe(callControlId as string),
    onSuccess: () => {
      if (callControlId) {
        void qc.invalidateQueries({
          queryKey: callLogKeys.detail(callControlId),
        });
      }
    },
  });
}

// ─── Action Needed (call follow-ups) ─────────────────────────────────────────

/**
 * One tab of the Action Needed queue.
 *
 * `keepPreviousData` is deliberate: switching Open → Cold is a different
 * question, but paging within a tab is not, and blanking the grid on every page
 * turn is what makes a worklist feel slow to work through.
 */
/**
 * Everything the Reports tab shows, in one request.
 *
 * Deliberately NOT a query per widget: two requests a moment apart can
 * disagree if a row closes between them, and a dashboard whose cards do not
 * add up is worse than none. Same SSE-or-poll fallback as the queue, so a
 * resolved call moves the report without a refresh.
 */
/**
 * The Reports page, as one answer. Every card reads this, so no two can
 * disagree. Refetched with the call log's own `call.*` invalidation; the
 * server caches it for five minutes, so the poll is cheap.
 */
export function useCallReports(params: CallReportsParams, options: { enabled?: boolean } = {}) {
  const sseOpen = useSseConnected();
  return useApiQuery({
    queryKey: callLogKeys.reports(params as unknown as Record<string, unknown>),
    queryFn: () => callsApi.reports(params),
    enabled: options.enabled ?? true,
    refetchInterval: sseOpen ? false : POLL_CALL_LOGS_MS,
    // No placeholderData: carrying the last report over while a new range
    // loads would put last week's numbers under this week's filters, and
    // Export would write them.
    showErrorToast: false,
  });
}

export function useActionNeededReport(
  params: ActionNeededReportParams,
  options: { enabled?: boolean } = {},
) {
  const sseOpen = useSseConnected();
  return useApiQuery({
    queryKey: callLogKeys.actionNeededReport(params as Record<string, unknown>),
    queryFn: () => callsApi.actionNeededReport(params),
    enabled: options.enabled ?? true,
    refetchInterval: sseOpen ? false : POLL_CALL_LOGS_MS,
    showErrorToast: false,
  });
}

export function useActionNeeded(params: ListCallFollowUpsParams) {
  const sseOpen = useSseConnected();
  return useApiQuery({
    queryKey: callLogKeys.actionNeededList(params as Record<string, unknown>),
    queryFn: () => callsApi.actionNeeded(params),
    // Same fallback as the call list: `call.*` SSE events invalidate this
    // cache, and the poll only matters while that stream is down.
    refetchInterval: sseOpen ? false : POLL_CALL_LOGS_MS,
    showErrorToast: false,
  });
}

/** A list's params without the page: the scrolling lists own their paging. */
export type InfiniteListParams<T> = Omit<T, 'page'>;

async function asApiError<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (err) {
    throw isApiError(err) ? err : normalizeError(err);
  }
}

/**
 * What a scrolling list hands its screen: the rows so far, and one safe way
 * to ask for more.
 *
 * `loadMore` is the only thing a scroll handler may call, and it refuses
 * while a page is already on its way, after the last page, and after a
 * failed page. That last refusal matters: a scroll handler fires dozens of
 * times a second, and retrying a failed page from it would hammer a server
 * that is already failing. Retrying is the explicit `retryMore`, from a
 * button. `cancelRefetch: false` keeps a second call from aborting the
 * request already in flight — React Query's default would restart it on
 * every scroll event and the page might never land.
 */
export interface InfiniteList<TRow> {
  rows: TRow[];
  /** Server-side match count, for "114 calls". */
  total: number;
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreFailed: boolean;
  loadMore: () => void;
  retryMore: () => void;
  refetch: () => void;
}

function useInfiniteList<TPage, TRow>(
  query: {
    data: InfiniteData<TPage> | undefined;
    isLoading: boolean;
    isError: boolean;
    isFetchNextPageError: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: (options?: { cancelRefetch?: boolean }) => Promise<unknown>;
    refetch: () => Promise<unknown>;
  },
  rowsOf: (page: TPage) => TRow[],
  totalOf: (page: TPage) => number | undefined,
  keyOf: (row: TRow) => string,
): InfiniteList<TRow> {
  const { data, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = query;
  const rows = useMemo(
    () => flattenPages((data?.pages ?? []).map(rowsOf), keyOf),
    // `rowsOf`/`keyOf` are module-level functions; the pages are the input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );
  const firstPage = data?.pages[0];
  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    void fetchNextPage({ cancelRefetch: false });
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);
  const retryMore = useCallback(() => {
    if (isFetchingNextPage) return;
    void fetchNextPage({ cancelRefetch: false });
  }, [isFetchingNextPage, fetchNextPage]);

  return {
    rows,
    total: (firstPage ? totalOf(firstPage) : undefined) ?? rows.length,
    // A failed NEXT page is not a failed list: the rows already loaded stay,
    // and the failure is shown where the next page would have been.
    isLoading: query.isLoading,
    isError: query.isError && !isFetchNextPageError,
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    loadMoreFailed: isFetchNextPageError,
    loadMore,
    retryMore,
    refetch: () => void query.refetch(),
  };
}

/**
 * The Call log as one list that grows as you scroll.
 *
 * Same server paging as `useCallLogs`, same SSE invalidation (the key sits
 * under `callLogKeys.list`), same polling fallback. A refetch re-reads every
 * page loaded so far, so a live update keeps the whole scrolled list current
 * rather than only its top.
 */
export function useInfiniteCallLogs(
  params: InfiniteListParams<ListCallLogsParams>,
): InfiniteList<CallLogItem> {
  const sseOpen = useSseConnected();
  const query = useInfiniteQuery<
    CallLogsResponse,
    ApiError,
    InfiniteData<CallLogsResponse>,
    readonly unknown[],
    number
  >({
    queryKey: callLogKeys.infiniteList(params as Record<string, unknown>),
    queryFn: ({ pageParam }) => asApiError(() => callsApi.list({ ...params, page: pageParam })),
    initialPageParam: 1,
    getNextPageParam: nextCallLogsPage,
    refetchInterval: sseOpen ? false : POLL_CALL_LOGS_MS,
    meta: { suppressToast: true },
  });
  return useInfiniteList(
    query,
    (page) => page.data?.callLogs ?? [],
    (page) => page.data?.pagination?.totalCount,
    callLogRowKey,
  );
}

/**
 * The Action needed queue as one growing list, plus the tab counts.
 *
 * Under the queue's cache root, so every write that invalidates the queue
 * (resolve, assign, attempt, reopen…) refreshes all of it.
 */
export function useInfiniteActionNeeded(
  params: InfiniteListParams<ListCallFollowUpsParams>,
): InfiniteList<CallFollowUp> & { counts: CallFollowUpCounts | undefined } {
  const sseOpen = useSseConnected();
  const query = useInfiniteQuery<
    CallFollowUpsResponse,
    ApiError,
    InfiniteData<CallFollowUpsResponse>,
    readonly unknown[],
    number
  >({
    queryKey: callLogKeys.actionNeededInfinite(params as Record<string, unknown>),
    queryFn: ({ pageParam }) =>
      asApiError(() => callsApi.actionNeeded({ ...params, page: pageParam })),
    initialPageParam: 1,
    getNextPageParam: nextActionNeededPage,
    refetchInterval: sseOpen ? false : POLL_CALL_LOGS_MS,
    meta: { suppressToast: true },
  });
  const list = useInfiniteList(
    query,
    (page) => page.data?.rows ?? [],
    (page) => page.data?.pagination?.totalCount,
    followUpRowKey,
  );
  return { ...list, counts: query.data?.pages[0]?.data?.counts };
}

/**
 * Refresh what a follow-up write did OUTSIDE this queue.
 *
 * Scheduling, resolving, assigning and linking a caller all reach through to
 * the customer's `ContactTask` — opening one, moving its due date, handing it
 * to somebody else, or cancelling it. Those tasks are read by the contact's own
 * tasks panel and by the global task list, neither of which knows this screen
 * exists, so without this a match or a reschedule leaves them showing a task
 * state the server has already replaced.
 *
 * `contactId` is passed where the caller knows it; the global list is
 * invalidated either way, because a task can move between owners and that list
 * is filtered by assignee rather than by customer.
 */
function invalidateFollowUpTasks(qc: ReturnType<typeof useQueryClient>, contactId?: string | null) {
  void qc.invalidateQueries({ queryKey: taskKeys.all() });
  if (contactId) {
    void qc.invalidateQueries({
      queryKey: [...contactKeys.detail(contactId), 'tasks'],
    });
  }
}

/**
 * Every write invalidates the whole queue rather than the row it touched.
 *
 * Resolving a row does not just remove it from this tab — it moves it to Done
 * and changes two of the three counts. Patching one cache entry would leave the
 * badges lying until the next refetch.
 */
function useFollowUpMutation<TArgs>(mutationFn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: callLogKeys.actionNeeded() });
    },
  });
}

/**
 * Close a row, by outcome or by bare disposition.
 *
 * The sheet sends `outcomeId` — the tenant's own word for what the call was
 * worth, which carries the disposition the server stamps. The grid's quick
 * "mark resolved" sends `disposition`. The server requires exactly one.
 */
export function useResolveFollowUp() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (args: {
      callControlId: string;
      outcomeId?: string;
      disposition?: CallFollowUpDisposition;
      note?: string;
      /** A next step that survives the close — "this is what the call was
       *  worth, and here is what we still owe them". */
      followUp?: FollowUpCommitmentInput;
      /** Re-file a call that is already closed, without reopening it. */
      amend?: boolean;
      /** So the customer's own task list can be refreshed: the close either
       *  cancels the task behind the row or keeps it live. */
      contactId?: string | null;
    }) =>
      callsApi.resolveFollowUp(args.callControlId, {
        outcomeId: args.outcomeId,
        disposition: args.disposition,
        note: args.note,
        followUp: args.followUp,
        ...(args.amend ? { amend: true } : {}),
      }),
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: callLogKeys.actionNeeded() });
      void qc.invalidateQueries({
        queryKey: callLogKeys.followUpActivity(args.callControlId),
      });
      invalidateFollowUpTasks(qc, args.contactId);
    },
  });
}

/**
 * One call's follow-up state, fetched by the call rather than found in a list.
 *
 * Under the queue's root, so any write to the queue refreshes it — the panel
 * showing a row has to move when somebody assigns or closes it elsewhere.
 * Answers `data: null` for a call that never needed following up, which is
 * most of them.
 */
export function useFollowUp(callControlId: string | null) {
  return useApiQuery({
    queryKey: callLogKeys.followUp(callControlId ?? ''),
    queryFn: () => callsApi.followUp(callControlId!),
    enabled: Boolean(callControlId),
    showErrorToast: false,
  });
}

/** Everything that has happened to this call, newest first. */
export function useFollowUpActivity(callControlId: string | null) {
  return useApiQuery({
    queryKey: callLogKeys.followUpActivity(callControlId ?? ''),
    queryFn: () => callsApi.followUpActivity(callControlId!),
    enabled: Boolean(callControlId),
    showErrorToast: false,
  });
}

/**
 * Add a note.
 *
 * Invalidates the whole queue like its siblings, not just the note list: the
 * grid shows a note count and the latest line, so a note that only refreshed
 * its own panel would leave the row behind it reading "no notes".
 */
export function useAddFollowUpNote() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (args: { callControlId: string; body: string }) =>
      callsApi.addFollowUpNote(args.callControlId, args.body),
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: callLogKeys.actionNeeded() });
      void qc.invalidateQueries({
        queryKey: callLogKeys.followUpActivity(args.callControlId),
      });
    },
  });
}

/**
 * Commit to a next step: when, what, whose, and how urgent.
 *
 * Every write here also lands in the call's history, so the activity feed has
 * to be invalidated alongside the queue — otherwise the panel that just
 * scheduled something shows a timeline that does not mention it.
 */
export function useScheduleFollowUp() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (
      args: {
        callControlId: string;
        contactId?: string | null;
      } & FollowUpCommitmentInput,
    ) =>
      callsApi.scheduleFollowUp(args.callControlId, {
        at: args.at,
        action: args.action,
        assignedToUserId: args.assignedToUserId,
        priority: args.priority,
      }),
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: callLogKeys.actionNeeded() });
      void qc.invalidateQueries({
        queryKey: callLogKeys.followUpActivity(args.callControlId),
      });
      invalidateFollowUpTasks(qc, args.contactId);
    },
  });
}

/** Put a closed call back in the queue. */
export function useReopenFollowUp() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (args: { callControlId: string }) => callsApi.reopenFollowUp(args.callControlId),
    onSuccess: (_data, args) => {
      // Said out loud, unlike its siblings. Reopening is usually done from the
      // Done tab or from a detail panel, and the row's answer — reappearing in
      // Open — happens on a list the person is not looking at.
      toast.success('Call reopened', {
        description: 'It is back in the queue, under Open.',
      });
      void qc.invalidateQueries({ queryKey: callLogKeys.actionNeeded() });
      void qc.invalidateQueries({
        queryKey: callLogKeys.followUpActivity(args.callControlId),
      });
    },
  });
}

/**
 * Say which customer an unmatched caller actually is.
 *
 * Also invalidates the contact's own caches: linking opens the follow-up task
 * this caller could not have, and that task belongs on the customer's record.
 */
export function useLinkFollowUpContact() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (args: { callControlId: string; contactId: string }) =>
      callsApi.linkFollowUpContact(args.callControlId, args.contactId),
    onSuccess: (_data, args) => {
      toast.success('Caller matched to a customer', {
        description: 'Notes and follow-ups on this call now reach their record.',
      });
      // Linking can OPEN a task an unmatched caller could not have: a
      // follow-up scheduled against a stranger has no contact to hang one on,
      // and gets one the moment there is.
      invalidateFollowUpTasks(qc, args.contactId);
      void qc.invalidateQueries({ queryKey: callLogKeys.actionNeeded() });
      void qc.invalidateQueries({
        queryKey: callLogKeys.followUpActivity(args.callControlId),
      });
    },
  });
}

/**
 * Iris's suggested outcome for a call.
 *
 * The one Iris-backed read in Call Logs, and it is fetched only where the
 * outcome is being set — never for a voicemail row (the server refuses those
 * anyway) and never for the call log at large. Held for the session: Iris's
 * insight on a finished call does not change, so refetching it on every sheet
 * open would be a round trip for the same answer.
 */
export function useOutcomeSuggestion(callControlId: string | null, options: { enabled: boolean }) {
  return useApiQuery({
    queryKey: callLogKeys.outcomeSuggestion(callControlId ?? ''),
    queryFn: () => callsApi.outcomeSuggestion(callControlId!),
    enabled: options.enabled && Boolean(callControlId),
    staleTime: Infinity,
    // A missing insight is a normal answer, and the upstream read is
    // best-effort — a toast for it would fire on perfectly ordinary rows.
    showErrorToast: false,
    retry: false,
  });
}

// ─── Call outcomes (the tenant's own vocabulary) ─────────────────────────────

/**
 * The tenant's outcome list, archived rows included.
 *
 * Archived ones are needed to NAME an outcome on a follow-up closed under a
 * label the tenant has since retired; a picker filters them out itself. Held
 * for a while — the list changes about as often as anyone edits settings.
 */
export function useCallOutcomes(options: { enabled?: boolean } = {}) {
  return useApiQuery({
    queryKey: callOutcomeKeys.list(),
    queryFn: () => callOutcomesApi.list(),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60_000,
    showErrorToast: false,
  });
}

function useOutcomeMutation<TArgs>(mutationFn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: callOutcomeKeys.all() });
      // Renaming or archiving an outcome changes what every row in the queue
      // reads, since the label is resolved server-side onto the row.
      void qc.invalidateQueries({ queryKey: callLogKeys.actionNeeded() });
    },
  });
}

export function useCreateCallOutcome() {
  return useOutcomeMutation(
    (args: { label: string; polarity: CallOutcomePolarity; sortOrder?: number }) =>
      callOutcomesApi.create(args),
  );
}

/**
 * Deletes an outcome nothing references.
 *
 * `showErrorToast` stays ON, unlike its read siblings: a refused delete is the
 * server explaining that calls are filed under it and to archive instead, and
 * that message is the whole value of the response.
 */
export function useDeleteCallOutcome() {
  return useOutcomeMutation((args: { id: string }) => callOutcomesApi.remove(args.id));
}

export function useUpdateCallOutcome() {
  return useOutcomeMutation(
    (args: {
      id: string;
      label?: string;
      polarity?: CallOutcomePolarity;
      sortOrder?: number;
      isActive?: boolean;
    }) => {
      const { id, ...body } = args;
      return callOutcomesApi.update(id, body);
    },
  );
}

/**
 * Who a follow-up can be handed to. Only fetched for people who can assign
 * (`messages:edit`); the roster changes rarely, so it is held for a while.
 */
export function useFollowUpAssignees(options: { enabled: boolean }) {
  return useApiQuery({
    queryKey: callLogKeys.followUpAssignees(),
    queryFn: () => callsApi.followUpAssignees(),
    enabled: options.enabled,
    staleTime: 5 * 60_000,
    showErrorToast: false,
  });
}

export function useAssignFollowUp() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (args: {
      callControlId: string;
      userId: string | null;
      /** So the customer's task list follows the handover. */
      contactId?: string | null;
    }) => callsApi.assignFollowUp(args.callControlId, args.userId),
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: callLogKeys.actionNeeded() });
      void qc.invalidateQueries({
        queryKey: callLogKeys.followUpActivity(args.callControlId),
      });
      // Reassigning the call reassigns the task behind it, so a task list
      // filtered by owner is wrong until it refetches.
      invalidateFollowUpTasks(qc, args.contactId);
    },
  });
}

export function useRecordFollowUpAttempt() {
  return useFollowUpMutation((args: { callControlId: string; channel: 'call' | 'sms' }) =>
    callsApi.recordFollowUpAttempt(args.callControlId, args.channel),
  );
}
